/**
 * Référentiel d'exercices — 2ᵉ sous-onglet de **Musculation**
 * (`ARCHITECTURE_V2.md` §1.1, §4.6 ; specs §8.2 et §12.1).
 *
 * 🔴 **Il était en Bibliothèque, il n'y est plus — décision prise après usage
 * réel sur l'appareil.** Le §4.6 et le §8.2 des specs rangeaient les exercices
 * avec les modèles, les aliments et les recettes ; c'est cohérent sur le papier
 * (ce sont tous des objets de référentiel) et faux à l'usage : on ouvre un
 * exercice **en composant une routine** ou **en salle**, jamais en même temps
 * qu'on corrige un aliment. Le domaine musculation se trouvait coupé en deux
 * onglets, dont l'un ne parle que de nutrition.
 *
 * C'est le même arbitrage que le §4.6 fait déjà pour les routines — « l'objet
 * va là où se trouve le geste qui l'emploie » — appliqué une fois de plus, et
 * dans le même sens.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 LA RECHERCHE EST LOCALE, ET C'EST TOUTE L'ÉTAPE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Contrairement aux aliments et aux recettes tels qu'ils étaient écrits jusqu'à
 * cette étape, cet écran ne requête **jamais** avec le terme dans la clé de
 * cache. Il charge la liste entière **une fois** (`EXERCISES_KEY`, persistée) et
 * la filtre en mémoire (`lib/localSearch.ts`). Deux conséquences, et ce sont
 * celles que le §3.1 promet :
 *
 *   - chercher fonctionne **réseau coupé**, y compris sur un terme jamais tapé
 *     auparavant ;
 *   - chaque frappe est instantanée, sans débounce ni aller-retour.
 *
 * Le classement vient de `shared/search.ts`, le **même** module que les routes
 * du serveur : le résultat ne change pas d'ordre selon qu'on a du réseau ou non.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LES IMAGES
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 🔴 **Aucun préchargement global.** ≈ 60 Ko × 1 746 images : précacher le jeu
 * entier remplirait le quota de stockage de la PWA pour des exercices qu'on ne
 * fait jamais (§1.1). Le service worker les met en cache **à la consultation**
 * (`CACHE_MEDIAS`, non versionné), et rien ici ne provoque de chargement en
 * masse : la liste affiche une vignette par ligne, en `loading="lazy"`, donc le
 * navigateur ne demande que ce qui entre à l'écran. Le préchargement **ciblé**
 * d'une routine (~8 exercices ≈ 1 Mo) est le sujet de l'**étape 6** — c'est
 * l'ouverture d'une séance qui le déclenchera, pas la consultation d'une liste.
 *
 * ⚠️ **La même vignette sert dans le sélecteur d'exercice d'une routine**
 * (`ExerciseThumb`), depuis les retours du 28/08/2026 : reconnaître un exercice
 * à sa silhouette est *le* geste qu'on fait en composant.
 *
 * ⚠️ **Un exercice sans image n'a pas de cadre vide** : la vignette n'est pas
 * rendue du tout, et la ligne se referme dessus. C'est l'exigence explicite du
 * §1.1, et c'est le cas nominal d'un exercice personnel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { ExerciseSummaryDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import {
  deleteExercise,
  exerciseKey,
  EXERCISES_KEY,
  fetchExercises,
  patchExercise,
  putExercise,
} from '../../api/exercises';
import { Button, Card, GlyphSearch, GlyphStar, TextField } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { newClientId } from '../../lib/dates';
import { exerciseFacets, searchExercisesLocally } from '../../lib/localSearch';
import { ExerciseEditorSheet } from './ExerciseEditor';
import { ExerciseFilterBar } from './ExerciseFilterBar';
import {
  filtersOf,
  NIVEAU_FR,
  NO_EXERCISE_FILTER,
  type ExerciseFilterState,
} from './exerciseFilters';
import { ExerciseFacts } from './ExerciseFacts';
import { ExerciseThumb } from './ExerciseThumb';
import {
  draftOfExercise,
  emptyExerciseDraft,
  inputOfExerciseDraft,
  type ExerciseDraft,
} from './exerciseDraft';

type Editing =
  | { mode: 'create'; clientId: string }
  | { mode: 'edit'; clientId: string; name: string };

function ExerciseRow({
  exercise,
  onOpen,
  onToggleFavorite,
}: {
  exercise: ExerciseSummaryDTO;
  onOpen: () => void;
  onToggleFavorite: () => void;
}) {
  const muscles = exercise.primaryMuscles.join(', ');
  const details = [
    exercise.equipment,
    NIVEAU_FR[exercise.level] ?? exercise.level,
    exercise.mechanic === 'compound' ? 'polyarticulaire' : exercise.mechanic === 'isolation' ? 'isolation' : null,
  ].filter((item): item is string => item !== null && item !== '');

  return (
    <li className="n-list__row">
      <Button
        variant="ghost"
        shape="circle"
        className={exercise.isFavorite ? 'n-star' : 'n-star n-star--off'}
        aria-pressed={exercise.isFavorite}
        aria-label={
          exercise.isFavorite
            ? `Retirer ${exercise.name} des favoris`
            : `Mettre ${exercise.name} en favori`
        }
        onClick={onToggleFavorite}
      >
        <GlyphStar filled={exercise.isFavorite} />
      </Button>

      <ExerciseThumb mediaKey={exercise.mediaKey} imageCount={exercise.imageCount} />

      <button type="button" onClick={onOpen} className="n-list__main">
        <span className="n-list__name">{exercise.name}</span>
        <span className="n-list__meta">
          {muscles === '' ? details.join(' · ') : [muscles, ...details].join(' · ')}
          {/* La provenance se lit, elle ne se devine pas à une couleur. */}
          {exercise.source === 'perso' && ' · perso'}
        </span>
      </button>
    </li>
  );
}

/**
 * Détail d'un exercice — la fiche, plus les deux actions de la bibliothèque.
 *
 * 🔴 **Le corps de la fiche a été EXTRAIT dans `ExerciseFacts`** *(29/08/2026)*,
 * parce qu'il a un second consommateur : la séance en direct l'ouvre en feuille
 * quand on touche le nom ou la vignette d'un exercice. Extrait plutôt que
 * recopié — deux implémentations de la même vue finissent par diverger, et le
 * symptôme serait qu'un exercice se lise autrement selon l'endroit d'où on
 * l'ouvre.
 *
 * Ce qui reste ici est ce qui n'appartient qu'à la bibliothèque : « Modifier »
 * et « Supprimer ». On ne modifie pas le référentiel au milieu d'une séance.
 */
function ExerciseDetail({
  exercise,
  onEdit,
  onDelete,
  removing,
}: {
  exercise: ExerciseSummaryDTO;
  onEdit: () => void;
  onDelete: () => void;
  removing: boolean;
}) {
  return (
    <div className="n-page">
      <ExerciseFacts exercise={exercise} />

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button variant="secondary" shape="rounded" block onClick={onEdit}>
          Modifier
        </Button>
        <Button variant="danger" shape="rounded" block disabled={removing} onClick={onDelete}>
          Supprimer
        </Button>
      </div>
    </div>
  );
}

export function ExercisesSection() {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  /*
   * ⚠️ **Un seul objet de filtres, piloté par `ExerciseFilterBar`** — le même
   * composant que le sélecteur d'exercice d'une routine. Trois `useState`
   * séparés auraient marché ; un état unique est ce qui rend le partage
   * possible sans que les deux écrans se mettent à diverger.
   */
  const [filtres, setFiltres] = useState<ExerciseFilterState>(NO_EXERCISE_FILTER);
  const [opened, setOpened] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState<ExerciseDraft>(emptyExerciseDraft);

  /*
   * 🔴 **Une seule requête, sans le terme dans la clé.** C'est ce qui rend la
   * recherche possible hors ligne : la réplique est restaurée par
   * `lib/persist.ts` au démarrage, et le filtre ne demande rien à personne.
   *
   * ⚠️ Pas de débounce non plus, contrairement aux aliments : il n'existe que
   * pour épargner des appels réseau (§5.2), et il n'y en a plus.
   */
  const exercises = useQuery({ queryKey: EXERCISES_KEY, queryFn: fetchExercises });

  /*
   * ⚠️ `useMemo` sur le repli aussi : `exercises.data ?? []` fabrique un tableau
   * neuf à chaque rendu tant que la requête n'a rien, ce qui referait tourner
   * les deux mémos en dessous pour rien. Sans conséquence visible ici, mais
   * c'est le genre de détail qui coûte cher le jour où la liste grossit.
   */
  const liste = useMemo(() => exercises.data ?? [], [exercises.data]);
  const facettes = useMemo(() => exerciseFacets(liste), [liste]);
  /*
   * ⚠️ **Les exercices du jeu importé, pas tous les exercices.** La note de pied
   * cite `free-exercise-db` et sa licence : y compter les exercices que
   * l'utilisateur a créés lui-même attribuerait au domaine public des fiches
   * qui n'en viennent pas. Détail, mais c'est une phrase sur une licence.
   */
  const nbBase = useMemo(() => liste.filter((row) => row.source === 'base').length, [liste]);
  const resultats = useMemo(
    () => searchExercisesLocally(liste, filtersOf(filtres, term)),
    [liste, filtres, term],
  );
  const ouvert = resultats.find((row) => row.clientId === opened) ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: EXERCISES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['exercise'] });
  };

  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('Aucun exercice en cours d’édition.');
      return putExercise(editing.clientId, inputOfExerciseDraft(draft));
    },
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const favorite = useMutation({
    mutationFn: (row: ExerciseSummaryDTO) =>
      patchExercise(row.clientId, { isFavorite: !row.isFavorite }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: deleteExercise,
    onSuccess: () => {
      refresh();
      setOpened(null);
    },
  });

  /* --- Vue détail ------------------------------------------------------ */

  if (ouvert) {
    return (
      <div className="n-page">
        <Button
          variant="ghost"
          shape="rounded"
          onClick={() => {
            setOpened(null);
          }}
        >
          ← Tous les exercices
        </Button>
        <h3 className="n-exo__title">{ouvert.name}</h3>

        {remove.isError && <ErrorNote>{remove.error.message}</ErrorNote>}

        <ExerciseDetail
          exercise={ouvert}
          removing={remove.isPending}
          onEdit={() => {
            /*
             * ⚠️ Le brouillon part du **détail en cache** quand il est là, du
             * résumé sinon. Sans cette précaution, ouvrir puis enregistrer un
             * exercice de base **effacerait ses instructions** : le résumé ne
             * les porte pas, et le `PUT` remplace la ressource entière.
             */
            const complet = queryClient.getQueryData(exerciseKey(ouvert.clientId));
            setDraft(
              complet === undefined
                ? { ...emptyExerciseDraft(), ...draftOfExercise({ ...ouvert, instructions: null }) }
                : draftOfExercise(complet as Parameters<typeof draftOfExercise>[0]),
            );
            setEditing({ mode: 'edit', clientId: ouvert.clientId, name: ouvert.name });
          }}
          onDelete={() => {
            remove.mutate(ouvert.clientId);
          }}
        />

        {editing && (
          <ExerciseEditorSheet
            key={editing.clientId}
            open
            title={editing.mode === 'edit' ? `Modifier « ${editing.name} »` : 'Nouvel exercice'}
            draft={draft}
            onDraftChange={setDraft}
            saving={save.isPending}
            onSubmit={() => {
              save.mutate();
            }}
            onClose={() => {
              setEditing(null);
            }}
          />
        )}
      </div>
    );
  }

  /* --- Liste ----------------------------------------------------------- */

  return (
    <div className="n-page">
      <TextField
        shape="pill"
        lead={<GlyphSearch />}
        aria-label="Chercher un exercice, un muscle ou un matériel"
        placeholder="Chercher un exercice, un muscle…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        selectOnFocus={false}
      />

      {/*
        ⚠️ **Le même composant que le sélecteur d'exercice d'une routine.** Les
        deux écrans doivent filtrer pareil ; deux barres jumelles auraient fini
        par diverger. Tout ce qu'il y avait à dire sur les facettes, les deux
        rangées et le vocabulaire est dans `ExerciseFilterBar.tsx`.
      */}
      <ExerciseFilterBar facets={facettes} value={filtres} onChange={setFiltres} />

      {exercises.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(exercises)} quoi="la base d’exercices" />
      {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}
      {favorite.isError && <ErrorNote>{favorite.error.message}</ErrorNote>}

      {!exercises.isPending && liste.length === 0 && readError(exercises) === null && (
        <p className="n-empty">
          Aucun exercice. La base libre de droits s’installe sur le serveur avec{' '}
          <code>infra/fetch-exercises.sh</code> ; en attendant, vous pouvez créer les vôtres.
        </p>
      )}

      {liste.length > 0 && resultats.length === 0 && (
        <p className="n-empty">Aucun exercice ne correspond.</p>
      )}

      {resultats.length > 0 && (
        <>
          <p className="n-hint" style={{ margin: 0 }}>
            {resultats.length === liste.length
              ? `${String(liste.length)} exercices`
              : `${String(resultats.length)} sur ${String(liste.length)}`}
          </p>
          <Card padding="none">
            <ul className="n-list">
              {/*
                ⚠️ Bornée à 60 lignes affichées. Ce n'est pas une pagination —
                la liste entière est en mémoire et le compteur au-dessus dit
                combien correspondent : c'est un plafond de **rendu**. Peindre
                873 lignes portant chacune une image ferait un écran qu'on ne
                parcourt de toute façon pas ; on affine sa recherche.
              */}
              {resultats.slice(0, 60).map((row) => (
                <ExerciseRow
                  key={row.clientId}
                  exercise={row}
                  onOpen={() => {
                    setOpened(row.clientId);
                  }}
                  onToggleFavorite={() => {
                    favorite.mutate(row);
                  }}
                />
              ))}
            </ul>
          </Card>
          {resultats.length > 60 && (
            <p className="n-hint" style={{ margin: 0 }}>
              {String(resultats.length - 60)} autres — affinez la recherche.
            </p>
          )}
        </>
      )}

      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          setDraft(emptyExerciseDraft());
          /*
           * 🔴 `clientId` tiré **à l'ouverture**, pas à l'enregistrement
           * (§2.3-b) : c'est l'adresse de la ressource, et l'avoir avant
           * l'écriture est ce qui rend la création composable hors ligne. Un
           * double tap sur « Enregistrer » réécrit alors la même ligne au lieu
           * d'en créer deux.
           */
          setEditing({ mode: 'create', clientId: newClientId() });
        }}
      >
        Nouvel exercice
      </Button>

      <p className="n-foot-note">
        {String(nbBase)} exercices viennent de <code>free-exercise-db</code>, dans le domaine
        public (Unlicense). Les images sont téléchargées à mesure que vous les consultez, et
        restent alors visibles sans réseau.
      </p>

      {editing && editing.mode === 'create' && (
        <ExerciseEditorSheet
          key={editing.clientId}
          open
          title="Nouvel exercice"
          draft={draft}
          onDraftChange={setDraft}
          saving={save.isPending}
          onSubmit={() => {
            save.mutate();
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
